import React, { ReactNode } from "react";
import styled, { css } from "styled-components";
import {
  getNewLookTokens,
  newLookPhoneType,
  newLookType,
} from "common/NewLook/tokens.ts";
import {
  NEW_LOOK_PHONE_GUTTER,
  NEW_LOOK_PAGE_GUTTER_VAR,
} from "common/NewLook/layout.ts";
import {
  NEW_LOOK_DRAWER_CLOSE_HIT,
  NEW_LOOK_DRAWER_HEADER_TOP,
} from "common/Drawers/Drawer.tsx";
import NewLookBackLink from "features/settings/NewLook/NewLookBackLink.tsx";

interface NewLookCheckoutLayoutProps {
  title?: ReactNode;
  onBack?: () => void;
  backLabel?: ReactNode;
  form: ReactNode;
  summary: ReactNode;
}

/**
 * The desktop checkout in the new look: a back link and the plan title, then
 * the plan form card next to a sticky payment summary card.
 */
const NewLookCheckoutLayout = ({
  title,
  onBack,
  backLabel,
  form,
  summary,
}: NewLookCheckoutLayoutProps) => (
  <Page>
    {(onBack || title) && (
      <Header>
        {onBack && (
          <NewLookBackLink onClick={onBack}>{backLabel}</NewLookBackLink>
        )}
        {title && <Title>{title}</Title>}
      </Header>
    )}
    <Grid>
      <FormCard>{form}</FormCard>
      <SummaryCard>{summary}</SummaryCard>
    </Grid>
  </Page>
);

export default NewLookCheckoutLayout;

interface NewLookMobileCheckoutProps {
  title?: ReactNode;
  form: ReactNode;
  summary: ReactNode;
  /** Hides the plan form while the keyboard is open. */
  hideForm?: boolean;
}

/**
 * The phone checkout in the new look: one scrolling column that reads top to
 * bottom (what you buy, the price, then how you pay), ending with the pay
 * button instead of the price rows.
 */
export const NewLookMobileCheckout = ({
  title,
  form,
  summary,
  hideForm,
}: NewLookMobileCheckoutProps) => (
  <Mobile>
    {title && <MobileTitle>{title}</MobileTitle>}
    {!hideForm && <MobileForm>{form}</MobileForm>}
    <MobileSummary $withForm={!hideForm}>{summary}</MobileSummary>
  </Mobile>
);

/**
 * Checkout controls shared by the desktop cards and the phone drawer: the
 * payment method switch, the agreement checkboxes and the pay button.
 */
export const newLookCheckoutStyles = css`
  /* The track, thumb and weights come from the central segmented rules. */
  .ant-segmented-item-label {
    min-height: 32px;
    line-height: 32px;
    font-size: 13.5px;
  }
  .ant-checkbox-wrapper {
    align-items: flex-start;
  }
  .ant-checkbox-wrapper .ant-checkbox {
    align-self: flex-start;
    margin-top: 2px;
  }
  .ant-checkbox-wrapper .ant-typography {
    color: ${(p) => p.theme.colors.secondaryText};
    font-size: 12.5px;
    line-height: 1.55;
  }
  .ant-btn-primary {
    height: 40px;
  }
`;

const Page = styled.div`
  width: 100%;
  max-width: 1120px;
  margin: 0 auto;
  padding: 16px ${NEW_LOOK_PAGE_GUTTER_VAR} 48px;
`;
const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  margin-bottom: 20px;
`;
const Title = styled.h1`
  margin: 0;
  ${newLookType.pageTitle};
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(340px, 400px);
  gap: 20px;
  align-items: start;
  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr) 340px;
  }
`;
const card = css`
  min-width: 0;
  padding: 24px;
  background: ${(p) => getNewLookTokens(p.theme).panel};
  border: ${(p) => p.theme.borders.panel};
  border-radius: ${(p) => p.theme.radii.card}px;
  box-shadow: ${(p) => p.theme.shadows.panel};
  ${newLookCheckoutStyles}
`;
const FormCard = styled.div`
  ${card}
`;
const SummaryCard = styled.div`
  ${card}
  position: sticky;
  top: 16px;
`;

const Mobile = styled.div`
  padding: ${NEW_LOOK_DRAWER_HEADER_TOP}px ${NEW_LOOK_PHONE_GUTTER}px 28px;
  ${newLookCheckoutStyles}
`;
/** The drawer's title row: the drawer's close button centres on it. */
const MobileTitle = styled.h2`
  display: flex;
  align-items: center;
  min-height: ${NEW_LOOK_DRAWER_CLOSE_HIT}px;
  margin: 0 ${NEW_LOOK_DRAWER_CLOSE_HIT - 8}px 12px 0;
  ${newLookPhoneType.headerTitle};
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const MobileForm = styled.div`
  min-width: 0;
`;
const MobileSummary = styled.div<{ $withForm: boolean }>`
  ${(p) =>
    p.$withForm &&
    css`
      margin-top: 20px;
      padding-top: 20px;
      border-top: ${p.theme.borders.lightest};
    `}
`;
